import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { apiService } from '../../services/api';

export type ContactLeadChannel = 'text' | 'whatsapp' | 'email';

type ContactLeadFormValues = {
  fullName: string;
  phone?: string;
  email?: string;
  message?: string;
};

interface ContactLeadModalProps {
  open: boolean;
  channel: ContactLeadChannel;
  onClose: () => void;
  contactPhone?: string;
  contactWhatsapp?: string;
  contactEmail?: string;
  vehicleId?: string;
  vehicleLabel?: string;
}

const channelLabels: Record<ContactLeadChannel, string> = {
  text: 'Text',
  whatsapp: 'WhatsApp',
  email: 'Email',
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const digitsOnly = (value: string): string => value.replace(/\D/g, '');

export const formatUsPhone = (value: string): string => {
  const digits = digitsOnly(value).slice(0, 10);
  if (digits.length <= 3) return digits ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const buildDeepLink = (
  channel: ContactLeadChannel,
  target: string,
  prefill: string
): string => {
  if (channel === 'email') {
    return `mailto:${target}?subject=${encodeURIComponent('Vehicle inquiry')}&body=${encodeURIComponent(
      prefill
    )}`;
  }

  const digits = digitsOnly(target);
  if (channel === 'whatsapp') {
    return `https://wa.me/${digits}?text=${encodeURIComponent(prefill)}`;
  }

  return `sms:${digits}?&body=${encodeURIComponent(prefill)}`;
};

const buildPrefill = (name?: string, vehicleLabel?: string, message?: string): string => {
  const trimmedName = name?.trim();
  const intro = trimmedName ? `Hi, I'm ${trimmedName}` : "Hi, I'm interested";
  const vehicleText = vehicleLabel ? ` — interested in the ${vehicleLabel}` : '';
  const defaultLine = `${intro}${vehicleText}.`;
  const trimmedMessage = message?.trim();
  return trimmedMessage ? `${defaultLine}\n\n${trimmedMessage}` : defaultLine;
};

const getTarget = (props: ContactLeadModalProps): string => {
  if (props.channel === 'email') return props.contactEmail || '';
  if (props.channel === 'whatsapp') return props.contactWhatsapp || props.contactPhone || '';
  return props.contactPhone || '';
};

const schemaForChannel = (channel: ContactLeadChannel) =>
  z
    .object({
      fullName: z.string().trim().min(2, 'Name is required'),
      phone: z.string().optional(),
      email: z.string().optional(),
      message: z.string().max(2000, 'Message is too long').optional(),
    })
    .superRefine((value, ctx) => {
      if (channel === 'email') {
        if (!value.email || !emailPattern.test(value.email.trim())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['email'],
            message: 'Enter a valid email address',
          });
        }
        return;
      }

      if (digitsOnly(value.phone || '').length < 10) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['phone'],
          message: 'Enter a 10-digit phone number',
        });
      }
    });

const ContactLeadModal: React.FC<ContactLeadModalProps> = (props) => {
  const { open, channel, onClose, vehicleId, vehicleLabel } = props;
  const target = getTarget(props);
  const label = channelLabels[channel];
  const schema = useMemo(() => schemaForChannel(channel), [channel]);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContactLeadFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { fullName: '', phone: '', email: '', message: '' },
  });

  const phoneValue = watch('phone') || '';

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  useEffect(() => {
    if (open) reset({ fullName: '', phone: '', email: '', message: '' });
  }, [open, reset, channel]);

  if (!open) return null;

  const openChannel = (values?: ContactLeadFormValues) => {
    if (!target) return;
    const prefill = buildPrefill(values?.fullName, vehicleLabel, values?.message);
    window.location.href = buildDeepLink(channel, target, prefill);
  };

  const submit = async (values: ContactLeadFormValues) => {
    const message = values.message?.trim() || null;
    try {
      await apiService.post('/website/leads/public', {
        full_name: values.fullName.trim(),
        ...(channel === 'email'
          ? { email: values.email?.trim() }
          : { phone: values.phone?.trim() }),
        channel,
        vehicle_id: vehicleId || null,
        message,
      });
    } catch (error) {
      // Best-effort capture: public handoff must not be blocked by API/rate-limit/network failures.
      console.warn('Public channel lead capture failed; continuing to dealer channel.', error);
    } finally {
      openChannel(values);
      onClose();
    }
  };

  const continueWithoutSharing = () => {
    openChannel();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      <button
        type="button"
        aria-label="Close contact form"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-lead-modal-title"
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-700">
              Quick {label}
            </p>
            <h2 id="contact-lead-modal-title" className="mt-1 text-2xl font-bold text-gray-900">
              Continue to {label}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Share your info so the dealership can follow up, or continue without sharing.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(submit)}>
          <div>
            <label
              className="block text-sm font-semibold text-gray-700"
              htmlFor="contact-lead-name"
            >
              Name
            </label>
            <input
              id="contact-lead-name"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
              autoComplete="name"
              {...register('fullName')}
            />
            {errors.fullName && (
              <p className="mt-1 text-sm text-red-600">{errors.fullName.message}</p>
            )}
          </div>

          {channel === 'email' ? (
            <div>
              <label
                className="block text-sm font-semibold text-gray-700"
                htmlFor="contact-lead-email"
              >
                Email
              </label>
              <input
                id="contact-lead-email"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                autoComplete="email"
                type="email"
                {...register('email')}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
            </div>
          ) : (
            <div>
              <label
                className="block text-sm font-semibold text-gray-700"
                htmlFor="contact-lead-phone"
              >
                Phone
              </label>
              <input
                id="contact-lead-phone"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                inputMode="tel"
                autoComplete="tel"
                value={phoneValue}
                onChange={(event) =>
                  setValue('phone', formatUsPhone(event.target.value), {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              />
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
            </div>
          )}

          <div>
            <label
              className="block text-sm font-semibold text-gray-700"
              htmlFor="contact-lead-message"
            >
              What are you interested in?
            </label>
            <textarea
              id="contact-lead-message"
              className="mt-1 min-h-24 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
              {...register('message')}
            />
            {errors.message && (
              <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
            )}
          </div>

          {!target && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              This contact channel is not configured yet.
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !target}
            className="w-full rounded-xl bg-blue-700 px-4 py-3 text-sm font-bold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {isSubmitting ? 'Saving…' : `Continue to ${label}`}
          </button>
        </form>

        <button
          type="button"
          onClick={continueWithoutSharing}
          disabled={!target}
          className="mt-4 w-full text-center text-sm font-semibold text-gray-600 underline hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-400"
        >
          Continue without sharing
        </button>
      </section>
    </div>
  );
};

export default ContactLeadModal;
